import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import openSocket from "../../services/socket-io";
import { AlertCircle, FilterX, Inbox } from "lucide-react";

import { Button } from "../ui/button";
import TicketListItem from "../TicketListItem";
import usePresence from "../../hooks/usePresence";
import usePrefTickets from "../../hooks/usePrefTickets";
import usePermissions from "../../hooks/usePermissions";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTickDeEspera } from "../../helpers/tempoDeEspera";

// O ticket que chega pelo socket é o mesmo para todo mundo e não carrega os
// agregados que só a listagem calcula (favorito da pessoa, mensagem que não
// saiu). Sobrescrever cru apagava a estrela e o selo de falha a cada mensagem
// nova que chegasse na conversa.
// Associação que o evento não trouxe, mas que continua valendo: o objeto
// anterior serve, e SÓ enquanto o id da coluna bater. A coluna (`queueId`,
// `whatsappId`) vem em todo evento, porque é campo do próprio ticket; a
// associação (`queue`, `whatsapp`) só vem de quem montou o include. Guardar
// pelo id impede o oposto do bug: manter o selo da fila anterior depois de o
// atendimento mudar de fila ou ficar sem nenhuma.
const manterAssociacao = (chave, coluna, anterior, ticket) => {
  if (chave in ticket) return ticket[chave];
  if (!ticket[coluna]) return null;

  return anterior?.[chave]?.id === ticket[coluna] ? anterior[chave] : null;
};

const manterAgregados = (anterior, ticket) => ({
  ...ticket,
  favoritado: ticket.favoritado ?? anterior?.favoritado,
  temFalha: ticket.temFalha ?? anterior?.temFalha,
  // Por que a linha apareceu na busca: só a listagem sabe, o socket não.
  buscaCasouPor: ticket.buscaCasouPor ?? anterior?.buscaCasouPor,
  buscaTrecho: ticket.buscaTrecho ?? anterior?.buscaTrecho,
  buscaMensagemId: ticket.buscaMensagemId ?? anterior?.buscaMensagemId,
  // Fila e conexão saem do mesmo buraco: o ticket que chega pelo socket é o
  // modelo cru na maioria dos emissores, sem `include`. Sem esta preservação o
  // spread apagava as duas — o selo da fila sumia a cada mensagem nova e o
  // canal caía no padrão mudo "whatsapp", que é o bug que o
  // `CreateMessageService` já tinha corrigido só para o caminho dele.
  queue: manterAssociacao("queue", "queueId", anterior, ticket),
  whatsapp: manterAssociacao("whatsapp", "whatsappId", anterior, ticket),
  // O atendente cai no MESMO buraco, e o efeito é pior: some o nome de quem
  // aceitou, e a conversa parece que voltou a não ter dono. Parecia aleatório
  // porque depende de ter chegado mensagem depois do aceite — é o evento da
  // mensagem que traz o ticket sem `user`.
  user: manterAssociacao("user", "userId", anterior, ticket),
  // Aqui a pergunta é "veio no evento?", não "veio preenchido?".
  //
  // `aguardandoDesde` nulo é uma resposta legítima — quer dizer "a equipe já
  // respondeu". Com `??`, o nulo caía no valor anterior e a etiqueta "Falta
  // responder" ficava acesa depois de respondida, até o F5. Quem emite manda
  // a chave quando sabe (`CreateMessageService`); quem não sabe não manda, e
  // aí o valor anterior continua valendo.
  aguardandoDesde:
    "aguardandoDesde" in ticket
      ? ticket.aguardandoDesde
      : anterior?.aguardandoDesde,
  // As etiquetas da PESSOA são montadas só pela listagem (`ListTicketsService`
  // as busca por página); emissor nenhum as manda pelo socket. Sem preservar,
  // QUALQUER evento do ticket — desmarcar uma etiqueta da conversa, chegar
  // mensagem, alguém aceitar — apagava as etiquetas da pessoa da linha, e elas
  // só voltavam no F5.
  etiquetasDoContato:
    "etiquetasDoContato" in ticket
      ? ticket.etiquetasDoContato
      : anterior?.etiquetasDoContato,
  // As da CONVERSA caem no mesmo buraco pelo outro lado: quem emite o modelo
  // cru (mensagem nova, por exemplo) não manda `tags`, e o spread apagava.
  // Lista vazia É resposta legítima — é o que chega quando a última etiqueta
  // foi tirada —, então a pergunta é "veio um array?", não "veio cheio?".
  tags: Array.isArray(ticket.tags) ? ticket.tags : anterior?.tags,
});

// Por onde o atendimento entra na lista: o mesmo critério do servidor
// (`ListTicketsService`), última mensagem e, sem ela, a criação.
const chaveDeOrdem = (ticket) =>
  new Date(ticket?.lastMessageAt || ticket?.createdAt || 0).getTime();

// Insere mantendo a ordem que veio do servidor, em vez de jogar no topo.
//
// O `unshift` cego punha no alto qualquer atendimento que chegasse pelo
// socket sem estar na página carregada — e ele ficava lá, à frente de
// conversas muito mais recentes, até o F5. Aparecia sempre que alguém mexia
// numa conversa antiga (transferir, encerrar, tentar responder), que é
// exatamente o que acontece com atendimento órfão.
//
// Mensagem nova continua subindo ao topo naturalmente: quem grava a mensagem
// atualiza o `lastMessageAt` do ticket antes de emitir, então a chave dele é
// a mais recente da lista.
const inserirNaOrdem = (state, ticket) => {
  const chave = chaveDeOrdem(ticket);
  const posicao = state.findIndex((t) => chaveDeOrdem(t) < chave);

  if (posicao === -1) {
    state.push(ticket);
  } else {
    state.splice(posicao, 0, ticket);
  }

  return state;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        // Sem promover o não lido ao topo: a ordem já vem pronta do servidor
        // (última mensagem), e subir aqui punha na frente conversa mais velha
        // só por estar não lida.
        state[ticketIndex] = ticket;
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  // Ressincronizar é trocar, não mesclar. Mesclando, quem já estava na tela
  // ficava na posição de antes da queda — com a mensagem nova na linha, mas lá
  // embaixo — e quem chegou no intervalo ia para o FIM da lista, atrás de
  // conversa parada há dias. Atendimento que saiu da aba no intervalo também
  // continuava ali, porque a mescla nunca tira ninguém.
  if (action.type === "REPLACE_TICKETS") {
    return [...action.payload];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = manterAgregados(state[ticketIndex], ticket);
    } else {
      inserirNaOrdem(state, ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      const atualizado = manterAgregados(state[ticketIndex], ticket);
      // Sai de onde está e volta pela ordem: mensagem nova traz
      // `lastMessageAt` fresco e cai no topo; um evento sem mensagem nova não
      // fura a fila.
      state.splice(ticketIndex, 1);
      inserirNaOrdem(state, atualizado);
    } else {
      inserirNaOrdem(state, ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;

    // TODAS as conversas da pessoa, não só a primeira: quem tem duas abertas
    // via o nome novo numa e o antigo na outra.
    state.forEach((t, i) => {
      if (t.contactId !== contact.id) return;

      state[i] = {
        ...t,
        contact,
        // A etiqueta da pessoa mudou: a linha reflete na hora, sem F5. O
        // evento traz o contato com as etiquetas dele (é o que
        // `ContactController.updateTags` e o etiquetar de um contato só
        // emitem); quando não traz, o que já estava continua valendo.
        etiquetasDoContato: Array.isArray(contact.tags)
          ? contact.tags
          : t.etiquetasDoContato,
      };
    });

    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsList = (props) => {
  const {
    status,
    searchParam,
    // Os dois campos da lista nova (ver useTickets).
    filtroContato,
    buscaMensagem,
    escopoBuscaMensagem,
    showAll,
    selectedQueueIds,
    // Filtro "Sem departamento" da gaveta ("incluir" | "excluir" | "so").
    semFila,
    updateCount,
    // Busca: recebe { open: 3, pending: 1, closed: 12 } para os botões de
    // situação mostrarem quanto há atrás de cada um.
    aoContarSituacoes,
    style,
    isGroup,
    vazio,
    ordemAZ,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites,
    soAdiados,
    ultimosDias,
    closeReasons,
    colunasVisiveis,
    itensVisiveis,
    densidade,
    botoesVisiveis,
    piscarPausa,
    recorrenteMinimo,
    // Seleção múltipla: a lista não guarda estado nenhum: quem manda é o
    // TicketsManager, porque a barra de ações vive lá em cima.
    selecionados,
    selecionando,
    aoSelecionar,
    // Quantos filtros da gaveta estão ligados, e como zerá-los: é o que
    // separa "não há conversa" de "o filtro escondeu as conversas".
    filtrosAtivos = 0,
    aoLimparFiltros,
    mostrarConexao,
    // Na busca a lista mistura situações: a linha precisa dizer qual é.
    mostrarStatus,
    // A aba está à vista? As inativas são escondidas com `display:none` (e não
    // com `visibility:hidden`, que continua fazendo layout): em produção são
    // quatro listas montadas e ~120 das ~160 linhas ficam invisíveis, dois
    // terços do DOM da tela recalculados para nada. Quem esconde é o
    // TicketsManager; aqui a prop só serve para devolver a rolagem, porque
    // `display:none` zera o `scrollTop` ao reaparecer. Padrão `true`: a lista
    // da busca não tem aba nenhuma.
    ativo = true,
  } = props;
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  // O escopo do cargo, a mesma régua que o backend usa em `podeAcessarTicket`.
  // Aqui ele só ESCONDE: quem barra de verdade é o servidor.
  //
  // Guardado num ref porque quem o consulta é o ouvinte do socket, criado uma
  // vez por conversa/aba: lendo a variável direto, o ouvinte ficava com o
  // valor do primeiro render — e no primeiro render o escopo ainda está
  // chegando (`/permissions/mine`), ou seja, `null`. Foi exatamente assim que
  // a primeira versão desta peneira deixou o atendimento do colega entrar.
  const { escopoDeTickets } = usePermissions();
  const escopoRef = useRef(escopoDeTickets);

  useEffect(() => {
    escopoRef.current = escopoDeTickets;
  }, [escopoDeTickets]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [
    status,
    searchParam,
    filtroContato,
    buscaMensagem,
    escopoBuscaMensagem,
    dispatch,
    showAll,
    selectedQueueIds,
    semFila,
    isGroup,
    visao,
    onlyFavorites,
    soAdiados,
    // Trocar a ordem refaz a lista do começo: a página 2 da ordem anterior
    // não continua a página 1 da nova.
    ordemAZ,
    // Filtro da barra também refaz: sem isso a lista antiga continuava na
    // tela e o resultado novo era MESCLADO nela — linha que o filtro acabara
    // de excluir seguia visível, e a página pedida era a do recorte anterior.
    tagIds,
    userIds,
    whatsappIds,
    ultimosDias,
    closeReasons,
  ]);

  const {
    tickets,
    hasMore,
    loading,
    erro,
    tentarDeNovo,
    contagemPorSituacao,
  } = useTickets({
    pageNumber,
    searchParam,
    filtroContato,
    buscaMensagem,
    escopoBuscaMensagem,
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
    semFila,
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites,
    soAdiados,
    ultimosDias,
    closeReasons,
    // A–Z é ordem do servidor: a lista é paginada, e ordenar só a página
    // carregada dava uma ordem diferente a cada rolagem.
    ordem: ordemAZ ? "az" : undefined,
    needsCount: false,
  });

  // Ligado pelo "resync": a próxima carga substitui a lista em vez de mesclar.
  const substituirNaProximaCarga = useRef(false);

  // Volta à página 1, que é onde está o que mudou no intervalo — rebuscar a
  // página em que a pessoa rolou não traria a conversa que subiu ao topo.
  const ressincronizar = useCallback(() => {
    substituirNaProximaCarga.current = true;
    setPageNumber(1);
    tentarDeNovo();
  }, [tentarDeNovo]);

  useEffect(() => {
    if (!status && !searchParam && !buscaMensagem) return;
    dispatch({
      type: substituirNaProximaCarga.current ? "REPLACE_TICKETS" : "LOAD_TICKETS",
      payload: tickets,
    });
    substituirNaProximaCarga.current = false;
  }, [tickets]);

  // O que já está na tela. O evento do socket carrega o ticket "cru", igual
  // para todo mundo: ele não diz se é favorito de quem está olhando nem de
  // quem é o recorte da aba. Guardar os ids visíveis é o que permite decidir
  // isso sem uma ida ao servidor por evento.
  const idsVisiveis = useRef([]);

  useEffect(() => {
    idsVisiveis.current = ticketsList.map((t) => t.id);
  }, [ticketsList]);

  useEffect(() => {
    const socket = openSocket();

    // O filtro de grupo vale também para o tempo real: sem isso um ticket de
    // grupo apareceria na aba de conversas individuais (e vice-versa).
    const matchesGroupFilter = (ticket) =>
      isGroup === undefined || Boolean(ticket.isGroup) === (isGroup === "true");

    // Os filtros da barra valem também para o que chega em tempo real. Sem
    // isto, um ticket novo entrava na lista mesmo sem a etiqueta (ou a
    // conexão, ou o atendente) filtrada: a pessoa filtrava por uma etiqueta e
    // via aparecer conversa que não tinha nada a ver com ela.
    const matchesTagFilter = (ticket) =>
      !tagIds?.length ||
      (ticket.tags || []).some((tag) => tagIds.includes(tag.id));

    const matchesWhatsappFilter = (ticket) =>
      !whatsappIds?.length || whatsappIds.includes(ticket.whatsappId);

    const matchesUserFilter = (ticket) =>
      !userIds?.length || userIds.includes(ticket.userId);

    // Filtro por motivo de encerramento (aba Resolvidos). Sem ele, um
    // atendimento fechado agora entrava na lista mesmo com outro motivo — a
    // pessoa filtrava por "sem retorno" e via aparecer um "resolvido".
    const matchesCloseReasonFilter = (ticket) =>
      !closeReasons?.length || closeReasons.includes(ticket.closeReason);

    // Cada lista é de um status só. A conexão do Socket.IO é uma para o app
    // inteiro, então TODA lista recebe o evento de TODO ticket — sem esta
    // conferência, uma mensagem nova num atendimento já aceito reinseria ele
    // na aba "Aguardando", e o mesmo cliente aparecia nas duas ao mesmo tempo.
    // `status` pode trazer mais de uma situação ("open,pending" — a aba
    // Grupos). Comparar a string inteira não casava com nenhum ticket: a aba
    // parava de receber tempo real e, pior, o ramo de baixo apagava a linha a
    // cada atualização, porque "open" !== "open,pending".
    const situacoes = String(status || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const matchesStatus = (ticket) =>
      !situacoes.length || situacoes.includes(ticket.status);

    // Eixo dono/setor: "Minhas" só aceita o que está na mão de quem entrou;
    // "Do departamento" aceita conversa de colega — é justamente o que ela mostra.
    const matchesVisao = (ticket) =>
      visao !== "minhas" || ticket.userId === user?.id;

    // Sem saber se é favorito, o critério seguro é "já estava na lista":
    // conversa recém-favoritada entra na próxima carga, e nenhuma
    // não-favorita se infiltra pelo tempo real.
    const matchesFavorito = (ticket) =>
      !onlyFavorites || idsVisiveis.current.includes(ticket.id);

    // Pausado sai da lista normal na hora, sem esperar recarga. A lista de
    // "Pausa" mostra tudo o que tem prazo, vencido inclusive — o vencido é o
    // que precisa de retorno, e some dali só quando alguém age.
    //
    // Mensagem por responder traz de volta: o cliente escreveu antes da hora,
    // e esconder isso até o prazo era a conversa ficar horas sem aparecer em
    // aba nenhuma. O prazo continua valendo (a linha mostra o relógio
    // amarelo); quem some é só a pausa silenciosa.
    const emPausa = (ticket) =>
      ticket.snoozedUntil &&
      new Date(ticket.snoozedUntil).getTime() > Date.now() &&
      !ticket.snoozeClienteEscreveu;

    const matchesSoneca = (ticket) =>
      soAdiados ? Boolean(ticket.snoozedUntil) : !emPausa(ticket);

    // O evento de socket chega pela sala da situação, que é da empresa
    // inteira: sem esta peneira, a aba "Do departamento" recebia o atendimento do
    // colega — com nome do cliente e prévia da mensagem — para quem tem
    // escopo "own" e não pode nem abrir a conversa (o GET devolve 403).
    //
    // Mesma leitura do `podeAcessarTicket`: "own" é só o que é meu; admin e
    // escopo de empresa continuam vendo tudo o que viam.
    const dentroDoEscopo = (ticket) => {
      if (user?.profile === "admin") return true;

      const escopo = escopoRef.current;

      if (escopo === "company") return true;
      // "own" é só o que é meu — inclusive o que ainda não tem dono, que o
      // `podeAcessarTicket` também nega para este escopo.
      if (escopo === "own") return ticket.userId === user?.id;

      // "queues"/"team" (e enquanto o escopo não chegou) seguem o filtro de
      // filas logo abaixo, que é o mesmo recorte da lista que veio do
      // servidor.
      return true;
    };

    // Filtrando, o socket só atualiza quem já está na tela: o evento não diz
    // se o ticket novo bate com o nome ou com a mensagem procurada.
    // Mesma regra do `montarRecorteDaAba`: sem filtro, o sem fila entra junto.
    const naFilaFiltrada = (ticket) =>
      ticket.queueId
        ? semFila !== "so" && selectedQueueIds.indexOf(ticket.queueId) > -1
        : semFila !== "excluir";

    const shouldUpdateTicket = (ticket) =>
      !searchParam &&
      (!(filtroContato || buscaMensagem) ||
        idsVisiveis.current.includes(ticket.id)) &&
      dentroDoEscopo(ticket) &&
      matchesStatus(ticket) &&
      matchesVisao(ticket) &&
      matchesFavorito(ticket) &&
      matchesSoneca(ticket) &&
      matchesGroupFilter(ticket) &&
      matchesTagFilter(ticket) &&
      matchesWhatsappFilter(ticket) &&
      matchesUserFilter(ticket) &&
      matchesCloseReasonFilter(ticket) &&
      (visao === "setor" ||
        !ticket.userId ||
        ticket.userId === user?.id ||
        showAll) &&
      naFilaFiltrada(ticket);

    const notBelongsToUserQueues = (ticket) => !naFilaFiltrada(ticket);

    // Voltou de uma queda (ou a aba passou muito tempo escondida): o que
    // aconteceu no intervalo não chega por evento nenhum. Buscar de novo é o
    // que impede a lista de ficar parada no tempo até alguém apertar F5.
    socket.on("resync", ressincronizar);

    socket.on("connect", () => {
      if (situacoes.length) {
        // Uma sala por situação. A aba Grupos pede "open,pending": entrar numa
        // sala com esse nome era entrar numa sala para onde o servidor nunca
        // emite — ela só recebia por carona das outras listas montadas.
        situacoes.forEach((situacao) => socket.emit("joinTickets", situacao));
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on("ticket", (data) => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      // Cada lista mostra um status. Quando o ticket muda de status ele
      // tem de sair desta lista e entrar na outra — antes ficava nas duas,
      // e os contadores das abas paravam de bater.
      // Adiar tem de tirar a linha da lista na hora (e trazê-la de volta na
      // aba "Adiados"): sem isto o atendimento adormecido só sumia no F5.
      if (data.action === "update" && !matchesSoneca(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      } else if (data.action === "update" && status && !matchesStatus(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      } else if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }

      // Na aba "Minhas", perder o dono (transferência, ou um colega que
      // puxou a conversa pela aba do setor) tem de tirar a linha da tela na
      // hora — senão ela continua ali dando a impressão de que o trabalho
      // ainda é meu.
      if (
        data.action === "update" &&
        visao === "minhas" &&
        data.ticket.userId !== user?.id
      ) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    });

    socket.on("appMessage", (data) => {
      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          // Mensagem que nasce como falha (ack -1, manual ou do robô) acende o
          // selo na hora: esperar a próxima carga da lista é esperar justamente
          // quem não abriu a conversa descobrir sozinho.
          payload:
            data.message?.ack === -1
              ? { ...data.ticket, temFalha: true }
              : data.ticket,
        });
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
    // Os filtros entram nas dependências: sem eles os ouvintes seguiriam
    // enxergando a seleção de quando foram registrados, e voltariam a deixar
    // passar ticket que o filtro atual exclui.
  }, [
    status,
    searchParam,
    filtroContato,
    buscaMensagem,
    showAll,
    user,
    selectedQueueIds,
    semFila,
    isGroup,
    tagIds,
    userIds,
    whatsappIds,
    visao,
    onlyFavorites,
    soAdiados,
    closeReasons,
    ressincronizar
  ]);

  // Filtro novo = lista nova: sem zerar, os resultados antigos ficariam
  // misturados com os do filtro.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [tagIds, userIds, whatsappIds, ultimosDias, closeReasons]);

  useEffect(() => {
    if (typeof aoContarSituacoes === "function" && contagemPorSituacao) {
      aoContarSituacoes(contagemPorSituacao);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contagemPorSituacao]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      // Com mais páginas por carregar o número é um piso, não o total: sem o
      // "+", a aba dizia "40" para 400 conversas esperando.
      updateCount(hasMore ? `${ticketsList.length}+` : ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList, hasMore]);

  // Fixados: preferência da PESSOA (some com o F5 de ninguém, e o colega do
  // lado tem os dele). Lido aqui, uma vez, e desce por prop — a lista redesenha
  // muito, e um hook por linha responderia sempre o mesmo.
  const [fixados] = usePrefTickets("ticketsFixados", []);

  // A ordem vem pronta do servidor (parâmetro `ordem`), inclusive no A–Z:
  // reordenar aqui só embaralharia de novo a cada página que chega.
  //
  // A ÚNICA exceção são os fixados, que a pessoa prendeu no topo: eles sobem
  // aqui, mantendo entre si a ordem que o servidor mandou.
  //
  // ponytail: eleva só o que já está carregado. Um fixado que ficou para trás
  // na paginação não é puxado para a primeira página — quem fixa, fixa o que
  // está acompanhando, e isso é recente. Se um dia precisar valer sempre, o
  // lugar é o ListTicketsService, com os ids indo na consulta.
  const listaVisivel = fixados.length
    ? [
        ...ticketsList.filter((t) => fixados.includes(t.id)),
        ...ticketsList.filter((t) => !fixados.includes(t.id)),
      ]
    : ticketsList;

  // Um socket de presença para a lista inteira, não um por item.
  const presencas = usePresence();

  // A conexão só aparece no item quando a empresa tem mais de uma — com
  // uma só, o nome se repetiria em todas as linhas sem informar nada.
  // Vem por prop: `useWhatsApps` busca `/whatsapp/` por chamada, e há uma
  // lista montada por aba — eram cinco requisições iguais na abertura da tela.

  // O tempo de espera precisa envelhecer na tela; sem isto "2 min" ficaria
  // congelado até a próxima mensagem.
  // O relógio é compartilhado com o cabeçalho da conversa para os dois nunca
  // mostrarem tempos diferentes do mesmo atendimento.
  useTickDeEspera();

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const caixaDeRolagem = useRef(null);
  const posicaoDaRolagem = useRef(0);

  // Voltar para a aba tem que devolver a pessoa onde ela parou de ler. Em
  // `useLayoutEffect` de propósito: no `useEffect` o navegador chega a pintar
  // a lista no topo antes de corrigir, e o salto aparece.
  useLayoutEffect(() => {
    if (!ativo) return;
    const caixa = caixaDeRolagem.current;
    if (caixa) caixa.scrollTop = posicaoDaRolagem.current;
  }, [ativo]);

  const handleScroll = (e) => {
    // Guardado a cada rolagem, e não na hora de esconder: elemento em
    // `display:none` responde `scrollTop` 0, então ler depois não adianta.
    posicaoDaRolagem.current = e.currentTarget.scrollTop;

    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      // A rolagem é de quem lê. Empurrar 100px para trás a cada página era o
      // salto que fazia a lista "pular" no meio da leitura; o guard de
      // `hasMore`/`loading` acima já basta para não pedir a mesma página duas
      // vezes.
      loadMore();
    }
  };

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={style}
    >
      <div
        ref={caixaDeRolagem}
        className="scrollbar-discreta flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {erro && !loading && ticketsList.length === 0 ? (
          // Falha não é caixa vazia. Antes as duas diziam a mesma coisa, e a
          // pessoa ficava esperando uma conversa que o servidor nem chegou a
          // contar.
          //
          // Só quando não há NADA carregado: falhar ao buscar a página 2 não
          // pode apagar as 40 conversas que já estão na tela.
          <div className="flex h-full flex-col items-center justify-center gap-1.5 px-8 py-16 text-center">
            <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">
              Não foi possível carregar os atendimentos
            </p>
            <p className="max-w-60 text-xs leading-relaxed text-muted-foreground">
              A lista não chegou até aqui. Verifique a conexão e tente de novo.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={tentarDeNovo}
            >
              Tentar de novo
            </Button>
          </div>
        ) : ticketsList.length === 0 && !loading ? (
          // Vazio de busca fica no topo, perto do campo onde a pessoa digitou.
          <div
            className={`flex h-full flex-col items-center gap-1.5 px-8 text-center ${
              vazio?.noTopo ? "justify-start pt-10" : "justify-center py-16"
            }`}
          >
            <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              {filtrosAtivos > 0 ? (
                <FilterX className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Inbox className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            {/* Lista vazia POR FILTRO não é caixa vazia: dizer "aceite uma
                conversa em Aguardando" para quem escondeu tudo com um filtro
                manda a pessoa procurar no lugar errado. */}
            <p className="text-sm font-medium">
              {filtrosAtivos > 0
                ? "Nenhum resultado com os filtros ativos"
                : vazio?.titulo || i18n.t("ticketsList.noTicketsTitle")}
            </p>
            <p className="max-w-60 text-xs leading-relaxed text-muted-foreground">
              {filtrosAtivos > 0
                ? `${filtrosAtivos} ${
                    filtrosAtivos === 1 ? "filtro aplicado está" : "filtros aplicados estão"
                  } escondendo conversas.`
                : vazio?.texto || i18n.t("ticketsList.noTicketsMessage")}
            </p>
            {filtrosAtivos === 0 && vazio?.extra}
            {filtrosAtivos > 0 && aoLimparFiltros && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={aoLimparFiltros}
              >
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          listaVisivel.map((ticket) => (
            <TicketListItem
              ticket={ticket}
              key={ticket.id}
              presenca={presencas[ticket.id]}
              mostrarConexao={mostrarConexao}
              mostrarStatus={mostrarStatus}
              termoBusca={searchParam || buscaMensagem}
              colunasVisiveis={colunasVisiveis}
              itensVisiveis={itensVisiveis}
              densidade={densidade}
              botoesVisiveis={botoesVisiveis}
              piscarPausa={piscarPausa}
              recorrenteMinimo={recorrenteMinimo}
              fixado={fixados.includes(ticket.id)}
              selecionado={Boolean(selecionados?.includes(ticket.id))}
              selecionando={selecionando}
              aoSelecionar={aoSelecionar}
            />
          ))
        )}
        {loading && <TicketsListSkeleton />}
      </div>
    </div>
  );
};

export default TicketsList;
